import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { RunAdvisorInsights } from '../../types/advisorInsights.types';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { aggregateOptimizerAudits } from '../audit/aggregateOptimizerAudits';
import { isFundingSegregationNotApplied } from '../audit/decisionPredicates';
import {
  buildPortfolioAdvisorInsights,
  buildPortfolioTaxInsight,
} from './buildPortfolioAdvisorInsights';
import {
  countEstimatedGainLines,
  countTaxableSellLines,
  estimateTaxableRealizedGainCents,
} from './estimateTaxableRealizedGainCents';
import { resolveAdvisorSituationKey } from './formatAdvisorInsightText';

export type {
  RunAdvisorInsights,
  RunSituationCount,
} from '../../types/advisorInsights.types';

const percentile = (
  sortedValues: number[],
  percentileRank: number,
): number | null => {
  if (sortedValues.length === 0) {
    return null;
  }

  const index = Math.min(
    sortedValues.length - 1,
    Math.max(0, Math.ceil((percentileRank / 100) * sortedValues.length) - 1),
  );

  return sortedValues[index] ?? null;
};

const median = (values: number[]): number | null => {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }

  return sorted[mid] ?? null;
};

export const buildRunAdvisorInsights = (
  audits: OptimizerAuditV2Record[],
  reportsById: ReadonlyMap<string, PortfolioReport> = new Map(),
): RunAdvisorInsights | null => {
  if (audits.length === 0) {
    return null;
  }

  const rollup = aggregateOptimizerAudits(audits);

  let registeredSellCents = 0;
  let taxableSellCents = 0;
  let portfoliosWithSells = 0;
  let portfoliosWithTaxableSells = 0;
  let portfoliosWithSituations = 0;
  let portfoliosWithEstRealizedGain = 0;
  let estRealizedGainCents = 0;
  let incompleteGainLineCount = 0;
  let portfoliosWithIncompleteGainEstimate = 0;
  const turnoverValues: number[] = [];
  const situationCountsByStage = new Map<string, number>();

  for (const audit of audits) {
    const report = audit.portfolioReportId
      ? reportsById.get(audit.portfolioReportId)
      : undefined;
    const taxInsight = buildPortfolioTaxInsight(audit);
    const portfolioInsights = buildPortfolioAdvisorInsights(audit, report);

    if (taxInsight?.hasSells) {
      portfoliosWithSells += 1;
      registeredSellCents += taxInsight.registeredSellCents;
      taxableSellCents += taxInsight.taxableSellCents;

      if (taxInsight.taxableSellCents > 0) {
        portfoliosWithTaxableSells += 1;

        const gainEstimate = report
          ? estimateTaxableRealizedGainCents(audit, report)
          : undefined;

        if (gainEstimate) {
          const estimatedGainLineCount = countEstimatedGainLines(gainEstimate);
          incompleteGainLineCount += gainEstimate.incompleteLineCount;

          if (gainEstimate.incompleteLineCount > 0) {
            portfoliosWithIncompleteGainEstimate += 1;
          }

          if (estimatedGainLineCount > 0) {
            estRealizedGainCents += gainEstimate.estRealizedGainCents;
            portfoliosWithEstRealizedGain += 1;
          }
        } else {
          const missingLineCount = countTaxableSellLines(audit);
          if (missingLineCount > 0) {
            incompleteGainLineCount += missingLineCount;
            portfoliosWithIncompleteGainEstimate += 1;
          }
        }
      }
    }

    if (portfolioInsights?.turnoverPct != null) {
      turnoverValues.push(portfolioInsights.turnoverPct);
    }

    const stagesInPortfolio = new Set<string>();
    for (const decision of portfolioInsights?.decisions ?? []) {
      if (isFundingSegregationNotApplied(decision)) {
        continue;
      }

      stagesInPortfolio.add(resolveAdvisorSituationKey(decision));
    }

    if (stagesInPortfolio.size > 0) {
      portfoliosWithSituations += 1;
    }

    for (const stage of stagesInPortfolio) {
      situationCountsByStage.set(
        stage,
        (situationCountsByStage.get(stage) ?? 0) + 1,
      );
    }
  }

  const totalSellCents = registeredSellCents + taxableSellCents;
  const sortedTurnover = [...turnoverValues].sort((a, b) => a - b);

  const situationCounts = [...situationCountsByStage.entries()]
    .map(([stage, portfolioCount]) => ({ stage, portfolioCount }))
    .sort(
      (a, b) =>
        b.portfolioCount - a.portfolioCount || a.stage.localeCompare(b.stage),
    );

  return {
    rollup,
    registeredSellCents,
    taxableSellCents,
    portfoliosWithSells,
    portfoliosWithTaxableSells,
    portfoliosWithSituations,
    portfoliosWithTurnover: turnoverValues.length,
    taxShelteredSellPct:
      totalSellCents > 0 ? (registeredSellCents / totalSellCents) * 100 : null,
    turnoverMedianPct: median(turnoverValues),
    turnoverP90Pct: percentile(sortedTurnover, 90),
    situationCounts,
    estRealizedGainCents:
      portfoliosWithEstRealizedGain > 0 ? estRealizedGainCents : null,
    estRealizedGainIsComplete:
      portfoliosWithEstRealizedGain > 0 &&
      portfoliosWithIncompleteGainEstimate === 0,
    portfoliosWithEstRealizedGain,
    incompleteGainLineCount,
    portfoliosWithIncompleteGainEstimate,
  };
};
